import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Offcanvas, Form, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import Avatar from "components/common/Avatar";
import { LogoutUser } from "components/authentication/config/AuthApi";
import { useDispatch, useSelector } from "react-redux";
import IconButton from "components/common/IconButton";
import { Controller, useForm } from "react-hook-form";
import MultiSelect from "components/common/MultiSelect";
import paths from "routes/paths";
import BootCheckbox from "components/common/custom/forms/BootCheckbox";
import { Person } from "react-bootstrap-icons";
import { GetAllBranches } from "components/config/HeaderApi";
import { handleSelectedBU } from "components/config/HeaderSlice";

const ProfileDropdown = () => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.auth);
  const { loginedBranch, buSearch } = useSelector((state) => state.header);
  const { allPermissionType } = useSelector((state) => state.role);
  const branchSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) => item?.rolemngmttyp_name === "Branch"
  );

  const allBranches = JSON.parse(localStorage.getItem("branch_settings"));

  const navigate = useNavigate();
  const [showCanvas, setShowCanvas] = useState(false);

  const { control, watch, setValue } = useForm({
    defaultValues: {
      branch: userData?.employee?.branch_default?.branch_id || "",
      default_branch: false,
    },
  });
  const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));

  useEffect(() => {
    if (buSearch) {
      setValue("branch", selectedBranch); // set the branch field by buSearch.value
    }
  }, [buSearch, setValue]);

  // Set initial default_branch value
  useEffect(() => {
    if (userData?.employee?.branch_default?.branch_id) {
      const isDefaultBranch =
        selectedBranch?.value === userData.employee.branch_default.branch_id ||
        selectedBranch?.branch_id ===
        userData.employee.branch_default.branch_id;
      setValue("default_branch", isDefaultBranch);
    }
  }, [userData, watch("branch"), setValue]);

  const allowedBranchIds = userData?.employee?.branch_id
    ? userData?.employee?.branch_id
      ?.toString()
      ?.split(",")
      ?.map((id) => Number(id.trim()))
    : [];

  const filteredBranches = allBranches?.filter((branch) =>
    allowedBranchIds.length > 0
      ? allowedBranchIds.includes(Number(branch.branch_id))
      : true
  );

  const branchOptions =
    filteredBranches?.map((branch) => ({
      value: branch.branch_id,
      label: branch.branch_name,
    })) || [];

  const handleLogout = async () => {
    const response = await dispatch(LogoutUser()).unwrap();
    if (response?.success) {
      // go to login, replace history, and reload page
      localStorage.removeItem("selectedBranch");
      window.location.replace(paths.login);
    }
  };

  return (
    <>
      <IconButton
        bsPrefix="toggle"
        to="#!"
        className="d-flex p-0 nav-link"
        onClick={() => setShowCanvas(true)}
      >
        <Avatar
          src={userData?.employee?.profile_picture}
          name={userData?.employee?.emp_name}
        />
      </IconButton>

      <Offcanvas
        placement="end"
        show={showCanvas}
        style={{ maxWidth: "18rem" }}
        className="border-0"
        onHide={() => setShowCanvas(false)}
      >
        <Offcanvas.Header closeButton />
        <Offcanvas.Body className="scrollbar d-flex flex-column">
          {/* User Profile Section */}
          <div className="text-center d-flex flex-column align-items-center ">
            <Avatar
              src={userData?.employee?.profile_picture}
              name={userData?.employee?.emp_name}
              size="3xl"
              className="mb-3"
            />
            <h5 className="fs-9">{userData?.employee?.emp_name || ""}</h5>
            <p className="text-700 text-primary mb-1">
              {userData?.employee?.email || ""}
            </p>
            <p className="text-700 mb-1">{selectedBranch?.branch_name || ""}</p>
            <div className="d-flex flex-column mt-2 align-items-center justify-content-center mb-1 gap-1">
              <h6 className="fs-9 text-800">Branch Nphies ID</h6>
              <h6 className="fs-10 text-info">
                {selectedBranch?.branch_nphies_code || ""}
              </h6>
            </div>
            <div className="d-flex flex-column align-items-center justify-content-center mb-1 gap-0">
              <h6 className="fs-9 text-800">Branch CCHI</h6>
              <h6 className="fs-10 text-info">
                {selectedBranch?.branch_cchino || ""}
              </h6>
            </div>
          </div>

          <hr className="border-300 my-2" />

          {/* Branch Selection */}
          <div className="w-100 d-flex flex-column justify-content-center gap-1 mb-2">
            <label className="form-label">Branches</label>
            <OverlayTrigger
              placement="top"
              show={Number(branchSetting?.list) !== 1 ? undefined : false}
              overlay={
                <Tooltip id="branch-disabled-tooltip">
                  You don't have permission to switch branches
                </Tooltip>
              }
            >
              <div>
                <Controller
                  name="branch"
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      menuPlacement="auto"
                      closeMenuOnSelect={false}
                      isMulti={false}
                      size="sm"
                      options={branchOptions}
                      placeholder="Choose..."
                      disabled={Number(branchSetting?.list) !== 1}
                      value={
                        branchOptions.find(
                          (option) =>
                            option.value === selectedBranch?.branch_id ||
                            option.value === selectedBranch?.value
                        ) || selectedBranch
                      }
                      onChange={(selected) => {
                        field.onChange(selected?.value);
                        dispatch(
                          handleSelectedBU({
                            check: "search",
                            value: selected,
                          })
                        );
                        window.location.reload();
                        navigate("/");
                        // Automatically update default_branch when branch changes
                        const isDefault =
                          selected?.value ===
                          userData?.employee?.branch_default?.branch_id;
                        setValue("default_branch", isDefault);
                      }}
                    />
                  )}
                />
              </div>
            </OverlayTrigger>
          </div>

          <div>
            <BootCheckbox
              name="default_branch"
              label="Set Default Branch"
              control={control}
              size="lg"
              disabled={
                selectedBranch?.value ===
                userData?.employee?.branch_default?.branch_id ||
                selectedBranch?.branch_id ===
                userData?.employee?.branch_default?.branch_id
              }
            />
          </div>

          <hr className="border-300 my-3" />

          {/* Profile Button */}
          <Button
            as={React.forwardRef((props, ref) => (
              <div
                {...props}
                ref={ref}
                onClick={() => {
                  setShowCanvas(false);
                  navigate(paths.profile);
                }}
              />
            ))}
            variant="outline-primary"
            className="mb-2"
          >
            Show Profile
          </Button>

          {/* Logout Button */}
          <Button
            onClick={handleLogout}
            variant="outline-danger"
            className="mt-auto"
          >
            Logout
          </Button>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default ProfileDropdown;
